import React from "react";

/**
 * "Built on science" section — shared between /  (home) and /find-out.
 * On /find-out (variant="full"), also renders the academic-frameworks table
 * and the validation-roadmap footer callout.
 */
const CARDS = [
  {
    n: "01",
    title: "Grounded in published research",
    body:
      "Six established academic frameworks underpin the five assessments — from Ajzen's Theory of Planned Behaviour to Luthans' Psychological Capital model. The questionnaires are not proprietary inventions. They are adapted from validated instruments used in entrepreneurship psychology research worldwide.",
  },
  {
    n: "02",
    title: "Backward-definition methodology",
    body:
      "Every question was designed starting from a precise construct definition — not from intuition. Each item was stress-tested against all other questions to ensure it measures only what it claims to, and nothing else. No two items across 115 questions share the same psychological mechanism.",
  },
  {
    n: "03",
    title: "Statistically rigorous scoring",
    body:
      "Dimension scores use standard deviation-based thresholds — not arbitrary pass/fail bands. Reverse-scored items and behavioural anchors are built in across all 115 questions to reduce self-enhancement bias — a known problem in any self-report assessment of professional populations.",
  },
];

const FRAMEWORKS = [
  {
    stage: "S0",
    name: "Founder Signal Test",
    anchor:
      "Push-Pull Theory (Orhan & Scott, 2001) · Self-Determination Theory (Deci & Ryan, 1985) · Need for Achievement (McClelland, 1961) · Theory of Planned Behaviour (Ajzen, 1991)",
  },
  {
    stage: "S1",
    name: "Founder Reality Check",
    anchor: "Theory of Planned Behaviour — Perceived Behavioural Control dimension (Ajzen, 1991)",
  },
  {
    stage: "S2.1",
    name: "Founder Archetype",
    anchor: "Adaption-Innovation Inventory (Kirton, 1976) · RIASEC Vocational Model (Holland, 1959)",
  },
  {
    stage: "S2.2",
    name: "Founder Durability",
    anchor:
      "Psychological Capital — PsyCap (Luthans, Youssef & Avolio, 2007) · Grit Scale (Duckworth & Quinn, 2009)",
  },
  {
    stage: "S2.3",
    name: "Founder Capability",
    anchor:
      "Entrepreneurial Competency Framework (Man, Lau & Chan, 2002) · Self-Efficacy Theory (Bandura, 1977)",
  },
];

export default function BuiltOnScienceSection({ variant = "compact" }) {
  const isFull = variant === "full";
  return (
    <section className="bos-section" data-testid="built-on-science-section">
      <div className="bos-container">
        <div className="bos-hero">
          <div className="bos-hero__left">
            <div className="bos-eyebrow">
              <span className="bos-eyebrow__line" aria-hidden />
              <span className="bos-eyebrow__label">BUILT ON SCIENCE</span>
            </div>
            <h2 className="bos-title">
              Not a quiz.<br />
              <span className="bos-title__lead">A psychometrically-grounded diagnostic.</span>
            </h2>
          </div>
          <div className="bos-hero__right">
            <p className="bos-hero__body">
              Every dimension in Pivotbox maps to published research in entrepreneurship psychology,
              motivation science, and founder behaviour. The questionnaires are adapted from validated
              academic instruments — not built from intuition. The scoring uses standard
              deviation-based thresholds, not arbitrary bands.
            </p>
          </div>
        </div>

        <div className="bos-cards">
          {CARDS.map((c) => (
            <article className="bos-card" key={c.n} data-testid={`bos-card-${c.n}`}>
              <span className="bos-card__num">{c.n}</span>
              <h3 className="bos-card__title">{c.title}</h3>
              <p className="bos-card__body">{c.body}</p>
            </article>
          ))}
        </div>

        {isFull && (
          <>
            <div className="bos-table-label">ACADEMIC FRAMEWORKS BEHIND EACH ASSESSMENT</div>
            <div className="bos-table-wrap" role="table">
              <div className="bos-table-head" role="row">
                <span role="columnheader">Stage</span>
                <span role="columnheader">Assessment</span>
                <span role="columnheader">Academic anchor</span>
              </div>
              {FRAMEWORKS.map((f) => (
                <div className="bos-table-row" role="row" key={f.stage}>
                  <span className="bos-table-stage" role="cell">{f.stage}</span>
                  <span className="bos-table-name" role="cell">{f.name}</span>
                  <span className="bos-table-anchor" role="cell">{f.anchor}</span>
                </div>
              ))}
            </div>

            <div className="bos-callout" role="note" data-testid="bos-validation-callout">
              <span className="bos-callout__icon" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#2F7D57" strokeWidth="1.5" />
                  <path d="M12 8V13" stroke="#2F7D57" strokeWidth="1.6" strokeLinecap="round" />
                  <circle cx="12" cy="16.2" r="1" fill="#2F7D57" />
                </svg>
              </span>
              <p className="bos-callout__text">
                Pivotbox is currently in a structured validation roadmap — including{" "}
                <strong>exploratory factor analysis</strong>, <strong>Cronbach&rsquo;s alpha reliability
                testing</strong>, and a <strong>12-month predictive validity study</strong> tracking
                Founding Cohort outcomes against assessment scores. The instrument is built to the
                standard of academic psychometric development, applied to a practitioner context.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
